import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import Markdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { colorir, linguagemDoRotulo } from '../../editor/monaco';
import { caminhoDoLink } from '../../workspace/estado';
import { fatiarCaminhos } from './caminhos';
import { ImagemDaConversa, ehImagem, nomeDeAnexo } from './ImagemDaConversa';

/** Acima disto pintar custa mais do que ajuda. */
const MAXIMO_PARA_PINTAR = 20_000;

function textoDe(filhos: ReactNode): string {
  if (typeof filhos === 'string') return filhos;
  if (typeof filhos === 'number') return String(filhos);
  if (Array.isArray(filhos)) return filhos.map(textoDe).join('');
  return '';
}

/**
 * Um trecho de código dentro da conversa.
 *
 * Com cerca e linguagem (```ts) sai pintado pelo tokenizador do Monaco, que já
 * está no pacote; em linha (`assim`) fica só com o fundo do CSS. Se o destaque
 * falhar ou o trecho for grande demais, mostra o texto cru — uma conversa não
 * pode sumir porque a pintura não deu certo.
 *
 * O HTML injetado vem do próprio Monaco, que escapa o texto ao tokenizar: o
 * que volta são `span` com cor, nunca marcação de quem escreveu.
 */
function Codigo({
  className,
  children,
}: {
  className?: string;
  children?: ReactNode;
}): React.JSX.Element {
  const bruto = textoDe(children).replace(/\n$/, '');
  const rotulo = /language-([\w+-]+)/.exec(className ?? '')?.[1] ?? '';
  const [html, setHtml] = useState('');

  useEffect(() => {
    setHtml('');
    if (!rotulo || bruto.length > MAXIMO_PARA_PINTAR) return;
    let vivo = true;
    void colorir(bruto, linguagemDoRotulo(rotulo)).then((pintado) => {
      if (vivo) setHtml(pintado);
    });
    return () => {
      vivo = false;
    };
  }, [bruto, rotulo]);

  if (html) return <code className={className} dangerouslySetInnerHTML={{ __html: html }} />;
  return <code className={className}>{bruto}</code>;
}

/**
 * Deixa passar caminho absoluto do Windows; o resto segue a regra de sempre.
 *
 * O `react-markdown` tem um saneador de URL proprio, alem do rehype: ele
 * descarta o que nao for esquema conhecido, e `C:` parece esquema tanto quanto
 * `mailto:`. Sem esta excecao o `href` chegava vazio ao componente de link e o
 * caminho virava texto morto — que e exatamente o defeito que este arquivo
 * existe para tirar.
 *
 * Abrir a excecao e seguro porque caminho nao navega: o componente de link
 * confere com `caminhoDoLink` e chama o editor. Nada aqui vira `href` que o
 * navegador siga.
 */
function transformarUrl(url: string): string {
  return /^[A-Za-z]:[\\/]/.test(url) ? url : defaultUrlTransform(url);
}

/**
 * Transforma caminho solto no texto em link, DEPOIS de sanear.
 *
 * Roda como plugin de rehype na segunda posição de propósito: o
 * `rehype-sanitize` derrubaria o `href` de `C:\projetos\notas.md`, porque a
 * letra de disco parece esquema de URL. Como estes nós são fabricados aqui a
 * partir de texto já saneado — nunca de HTML de quem escreveu —, gerá-los
 * depois não reabre a porta que o saneador fecha.
 *
 * `fatiarCaminhos` é conservador: só marca caminho do Windows com disco ou
 * caminho com barra e extensão. O resto do texto sai intocado.
 */
function caminhosClicaveis() {
  type No = { type: string; tagName?: string; value?: string; children?: No[]; properties?: unknown };

  const andar = (no: No): void => {
    if (!Array.isArray(no.children)) return;
    // Não aninhar link dentro de link, nem mexer em código.
    if (no.type === 'element' && (no.tagName === 'a' || no.tagName === 'code')) return;

    const saida: No[] = [];
    let mudou = false;

    for (const filho of no.children) {
      if (filho.type !== 'text' || typeof filho.value !== 'string') {
        andar(filho);
        saida.push(filho);
        continue;
      }
      const fatias = fatiarCaminhos(filho.value);
      if (fatias.length === 1 && !fatias[0]?.caminho) {
        saida.push(filho);
        continue;
      }
      mudou = true;
      for (const fatia of fatias) {
        saida.push(
          fatia.caminho
            ? {
                type: 'element',
                tagName: 'a',
                properties: { href: fatia.caminho },
                children: [{ type: 'text', value: fatia.texto }],
              }
            : { type: 'text', value: fatia.texto },
        );
      }
    }

    if (mudou) no.children = saida;
  };

  return (arvore: No): void => andar(arvore);
}

/**
 * Texto da conversa renderizado como markdown.
 *
 * É o mesmo trio do preview de arquivo — `react-markdown`, `remark-gfm` para
 * tabela e `rehype-sanitize` sempre ligado. Sanitizar importa ainda mais aqui:
 * o texto vem de uma máquina e pode repetir algo que o Claude leu na internet,
 * e HTML cru rodaria no nosso domínio com a sessão de quem está lendo.
 */
/**
 * O link interno que a sessão mestre escreve: `[título](/sessoes/<id>)`.
 * `nova` fica de fora: é a rota de criação, não uma conversa.
 */
const LINK_DE_SESSAO = /^\/sessoes\/(?!nova$)([A-Za-z0-9_-]{4,64})$/;

export function TextoMarkdown({
  texto,
  claro = false,
  cwd = '',
  sessaoId = '',
  aoAbrirArquivo,
  aoIrParaSessao,
}: {
  texto: string;
  /** Bolha de fundo claro (a sua): as cores do markdown invertem. */
  claro?: boolean;
  /** A pasta da sessão, para resolver link relativo de arquivo. */
  cwd?: string;
  /** A conversa em que este texto está: é ela que resolve o anexo. */
  sessaoId?: string;
  aoAbrirArquivo?: (caminho: string) => void;
  /** Link `/sessoes/<id>` troca de conversa sem recarregar (F13). */
  aoIrParaSessao?: (sessaoId: string) => void;
}): React.JSX.Element {
  /**
   * A função de abrir arquivo vive numa referência.
   *
   * Quem chama passa uma arrow escrita na hora, então a identidade dela muda a
   * cada render. Se ela entrasse nas dependências do `useMemo` abaixo, o
   * componente de link seria recriado do mesmo jeito — e o problema que este
   * arranjo existe para resolver voltaria inteiro.
   */
  const abrir = useRef(aoAbrirArquivo);
  abrir.current = aoAbrirArquivo;
  const irParaSessao = useRef(aoIrParaSessao);
  irParaSessao.current = aoIrParaSessao;

  /**
   * Link de arquivo abre no editor; link de verdade abre noutra aba.
   *
   * Nenhum dos dois pode navegar dentro do painel. O Claude escreve caminho
   * relativo o tempo todo (`docs/roteiros/pat-da-vps.md`), e como link comum
   * isso tirava a pessoa da conversa para um endereço que não existe.
   */
  const componentes = useMemo(() => {
  function Ligacao({
    href,
    children,
  }: {
    href?: string;
    children?: ReactNode;
  }): React.JSX.Element {
    /**
     * Link interno de sessão ANTES do teste de caminho: `/sessoes/abc` parece
     * caminho relativo para o `caminhoDoLink`, e sem este ramo virava um botão
     * de abrir o arquivo inexistente `sessoes\abc` — falha silenciosa para o
     * pior lado. Sem o handler à mão (contexto sem navegação), vira `<a>` de
     * verdade: a rota `/sessoes/:id` redireciona para o workspace.
     */
    const interno = LINK_DE_SESSAO.exec(href ?? '');
    if (interno) {
      const destino = interno[1] ?? '';
      if (irParaSessao.current) {
        return (
          <button
            type="button"
            onClick={() => irParaSessao.current?.(destino)}
            title="Abrir esta conversa"
            className="underline decoration-dotted underline-offset-2"
          >
            {children}
          </button>
        );
      }
      return <a href={href}>{children}</a>;
    }

    const arquivo = caminhoDoLink(cwd, href ?? '');

    /**
     * Imagem anexada aparece como imagem, no lugar do caminho.
     *
     * É o único caso em que o caminho não vira botão de abrir arquivo: a pasta
     * de anexos do daemon está fora da allowlist de raízes, então esse botão
     * levava a um arquivo que o painel nunca conseguiria ler — clicar não fazia
     * nada, e nada é a pior resposta possível.
     */
    const anexo = href && ehImagem(href) ? nomeDeAnexo(href) : null;
    if (anexo && sessaoId) {
      /**
       * A legenda não é o texto do link: ali está o caminho inteiro do disco,
       * e o nome do arquivo é o identificador que o servidor sorteou. Nenhum
       * dos dois diz nada a quem lê — e viraria o texto alternativo da imagem.
       */
      return <ImagemDaConversa sessaoId={sessaoId} nome={anexo} legenda="imagem anexada" />;
    }

    if (arquivo && abrir.current) {
      return (
        <button
          type="button"
          onClick={() => abrir.current?.(arquivo)}
          title={'Abrir ' + arquivo}
          className="underline decoration-dotted underline-offset-2"
        >
          {children}
        </button>
      );
    }
    if (href && /^https?:/i.test(href)) {
      return (
        <a href={href} target="_blank" rel="noreferrer">
          {children}
        </a>
      );
    }
    // Caminho sem editor à mão, ou esquema que não conhecemos: mostra o texto
    // e não navega. Sumir com o link seria pior que ele não levar a lugar
    // nenhum, mas levar para fora do painel é pior que os dois.
    return <span title={href}>{children}</span>;
  }

    return { code: Codigo, a: Ligacao };
    /**
     * Só `cwd` e `sessaoId` entram aqui, e é isso que conserta o defeito.
     *
     * O mapa de componentes ia inline no JSX, então `a` era uma função nova a
     * cada render — e para o React, componente de identidade nova é componente
     * OUTRO: ele desmontava e remontava a árvore inteira do link. Com a
     * conversa recarregando sozinha a cada giro das consultas de sessão e
     * permissão, isso fazia toda imagem anexada piscar de tempos em tempos,
     * sem que nada tivesse mudado.
     */
  }, [cwd, sessaoId]);

  return (
    <div className={'prose-remoto prose-chat' + (claro ? ' prose-clara' : '')}>
      <Markdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize, caminhosClicaveis]}
        urlTransform={transformarUrl}
        components={componentes}
      >
        {texto}
      </Markdown>
    </div>
  );
}
